import { EPaymentFrequency } from '@if/core/types'
import { formatCurrency, formatDate, normalizeNumber } from '@if/core/utils'
import { isPromoPlan, WorksheetFundingSummary, FundingSummaryRow } from '@src/components/WorksheetForm'
import { useEditTotalObligation } from '@src/containers/EditWorksheet/editWorksheet-hooks'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { Merchant } from '@src/data/types/Merchant'
import { formatPaymentPlanForSummary } from '@src/services/Formatter'
import { computeTotalCoverageFromFunding } from '@src/services/WorksheetComputer'
import { Control, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PersonalEditValues } from '../personalEditForm'

interface PersonalFundingSummaryProps {
  control: Control<PersonalEditValues>
  computedInfo: FundingComputedDto
  isComputing: boolean
  merchant: Merchant
  decisionInterestRate: number
  maxPmtAmount: number
}

// The legacy right-rail funding summary, rebuilt on the shared WorksheetFundingSummary rows.
const PersonalFundingSummary = ({
  control,
  computedInfo,
  isComputing,
  merchant,
  decisionInterestRate,
  maxPmtAmount,
}: PersonalFundingSummaryProps) => {
  const { t } = useTranslation()

  const [amountRequestedRaw, paymentPlanId, paymentFrequency, firstPaymentDate] = useWatch({
    control,
    name: ['amountRequested', 'paymentPlanId', 'paymentFrequency', 'firstPaymentDate'],
  })
  const amountRequested = normalizeNumber(amountRequestedRaw?.toString()) ?? 0

  const isVariableInterest = isPromoPlan(paymentPlanId)
  const selectedPlan = merchant.paymentPlans?.find((p) => (p.id as string) === paymentPlanId)

  const totalCoverage = computeTotalCoverageFromFunding(computedInfo)
  const totalObligation = useEditTotalObligation(amountRequested, computedInfo)

  // A Custom PromoPlan GUID isn't in merchant.paymentPlans (selectedPlan is undefined), so show
  // the applied rate returned by ComputeFunding; legacy promo plans keep their formatted label.
  const interestLabel = isVariableInterest
    ? selectedPlan
      ? formatPaymentPlanForSummary(selectedPlan)
      : `${computedInfo.interestRate ?? decisionInterestRate}%`
    : `${decisionInterestRate}%`

  const rows: FundingSummaryRow[] = [
    { key: 'loanAmount', label: `${t('worksheetCommon.loanAmount')}`, value: formatCurrency(amountRequested) },
    {
      key: 'interest',
      label: `${t('worksheetCommon.interest')} (${interestLabel})`,
      value: formatCurrency(computedInfo.totalInterestAmount),
    },
    {
      key: 'financeFee',
      label: `${t('worksheetCommon.financeFee')} (${computedInfo.lenderFeeRate ?? 0}%)`,
      value: formatCurrency(computedInfo.lenderFee ?? 0),
    },
    ...(isVariableInterest && selectedPlan
      ? [
          {
            key: 'merchantFee',
            label: `${t('worksheetCommon.merchantFee')} (${selectedPlan.merchantFeeRate}%)`,
            value: formatCurrency((selectedPlan.merchantFeeRate / 100) * amountRequested),
          },
        ]
      : []),
    { key: 'protectionPlan', label: t('worksheetCommon.protectionPlan'), value: formatCurrency(totalCoverage) },
    {
      key: 'totalObligation',
      label: t('worksheetCommon.totalObligation'),
      value: formatCurrency(totalObligation),
      emphasis: true,
    },
    {
      key: 'annualPercentageRate',
      label: t('worksheetCommon.annualPercentageRate'),
      value: `${computedInfo.effectiveRate ?? 0} %`,
    },
    { key: 'paymentHeader', label: t('worksheetCommon.payment'), header: true },
    { key: 'maxPmtAmount', label: t('worksheetCommon.maxPmtAmount'), value: formatCurrency(maxPmtAmount) },
    {
      key: 'paymentFrequency',
      label: t('worksheetCommon.paymentFrequency'),
      value: t(`EPaymentFrequency.${paymentFrequency ?? EPaymentFrequency.Monthly}`, { ns: 'enum' }),
    },
    {
      key: 'frequencyAmount',
      label: t('worksheetCommon.frequencyAmount'),
      value: formatCurrency(computedInfo.paymentForFrequency),
      emphasis: true,
    },
    {
      key: 'firstPaymentDate',
      label: t('worksheetCommon.firstPaymentDate'),
      value: firstPaymentDate ? formatDate(firstPaymentDate.toString()) : '',
    },
    {
      key: 'amountToBePaid',
      label: t('worksheetCommon.amountToBePaid'),
      value: formatCurrency(amountRequested),
      emphasis: true,
    },
  ]

  return (
    <WorksheetFundingSummary title={t('worksheetCommon.fundingSummary')} isComputing={isComputing} columns={[rows]} />
  )
}

export default PersonalFundingSummary
